import { useRef, useState } from 'react'
import { Upload } from 'lucide-react'

import { cn } from '@/shared/lib/utils'

/**
 * Reusable drag-and-drop file zone. Drop a file or click to browse.
 * Generic - used for CSV/Excel imports and any single-file upload.
 */
export function FileDropzone({
  accept,
  onFile,
  hint,
  disabled,
  className,
}: {
  /** Accept attribute, e.g. ".csv,.xlsx,.xls". */
  accept?: string
  onFile: (file: File) => void
  /** Small helper line under the prompt (e.g. accepted formats). */
  hint?: string
  disabled?: boolean
  className?: string
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  const handlePick = (file?: File | null) => {
    if (file && !disabled) onFile(file)
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          inputRef.current?.click()
        }
      }}
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled) setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragging(false)
        handlePick(e.dataTransfer.files?.[0])
      }}
      className={cn(
        'flex flex-col items-center justify-center gap-1.5 rounded-[8px] border border-dashed px-4 py-6 text-center outline-none transition-colors',
        dragging
          ? 'border-[var(--green)] bg-[var(--bg-subtle)]'
          : 'border-[var(--rule)] bg-[var(--bg-subtle)] hover:border-[var(--ink-3)]',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        className,
      )}
    >
      <Upload size={18} className="text-[var(--ink-3)]" />
      <span className="text-[12px] text-[var(--ink-2)]">
        Drag &amp; drop a file here, or{' '}
        <span className="text-[var(--green)]">browse</span>
      </span>
      {hint && (
        <span className="text-[11px] text-[var(--ink-3)]">{hint}</span>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          handlePick(e.target.files?.[0])
          e.target.value = ''
        }}
      />
    </div>
  )
}
